import React, { useState, useEffect, useRef } from 'react';
import { X } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';
import { resolverPreview } from '../utils/previewVariaveis';

const WhatsAppSimulator = ({ fluxo, onClose }) => {
  const { darkMode } = useTheme();
  const [messages, setMessages] = useState([]);
  const [currentNode, setCurrentNode] = useState(null);
  const [input, setInput] = useState('');
  const [nodes, setNodes] = useState([]);
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    if (fluxo?.etapas) {
      // Converter etapas para formato de nodes
      const convertedNodes = fluxo.etapas.map(etapa => ({
        id: etapa.id,
        type: etapa.tipo_etapa,
        data: {
          mensagem: etapa.mensagem_automatica,
          label: etapa.nome,
          opcoes: etapa.opcoes_menu,
          proxima_etapa_id: etapa.proxima_etapa_id,
          aguardar_resposta: etapa.aguardar_resposta
        }
      }));
      
      setNodes(convertedNodes);
      
      // Iniciar com primeira etapa
      if (convertedNodes.length > 0) {
        const firstNode = convertedNodes[0];
        setCurrentNode(firstNode);
        const msg = firstNode.data.mensagem || firstNode.data.label;
        setTimeout(() => {
          setMessages([{ text: msg, from: 'bot', node: firstNode }]);
        }, 300);
      }
    }
  }, [fluxo]);

  const handleReset = () => {
    setMessages([]);
    setInput('');
    if (nodes.length > 0) {
      const firstNode = nodes[0];
      setCurrentNode(firstNode);
      const msg = firstNode.data.mensagem || firstNode.data.label;
      setTimeout(() => {
        setMessages([{ text: msg, from: 'bot', node: firstNode }]);
      }, 300);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!input.trim()) return;
    
    setMessages(prev => [...prev, { text: input, from: 'user' }]);
    const userInput = input;
    setInput('');
    
    setTimeout(() => {
      if (currentNode?.type === 'menu' && currentNode?.data.opcoes) {
        const opcao = currentNode.data.opcoes.find(op => op.opcao === userInput.trim());
        if (opcao?.proxima_etapa_id) {
          const nextNode = nodes.find(n => n.id === opcao.proxima_etapa_id);
          if (nextNode) {
            setCurrentNode(nextNode);
            const msg = nextNode.data.mensagem || nextNode.data.label;
            setMessages(prev => [...prev, { text: msg, from: 'bot', node: nextNode }]);
            
            if (nextNode.data.aguardar_resposta === false && nextNode.data.proxima_etapa_id) {
              setTimeout(() => {
                const nextNode2 = nodes.find(n => n.id === nextNode.data.proxima_etapa_id);
                if (nextNode2) {
                  setCurrentNode(nextNode2);
                  const msg2 = nextNode2.data.mensagem || nextNode2.data.label;
                  setMessages(prev => [...prev, { text: msg2, from: 'bot', node: nextNode2 }]);
                }
              }, 800);
            }
          }
        } else {
          setMessages(prev => [...prev, { text: 'Opção inválida. Tente novamente.', from: 'bot' }]);
        }
      } else if (currentNode?.data.proxima_etapa_id) {
        const nextNode = nodes.find(n => n.id === currentNode.data.proxima_etapa_id);
        if (nextNode) {
          setCurrentNode(nextNode);
          const msg = nextNode.data.mensagem || nextNode.data.label;
          setMessages(prev => [...prev, { text: msg, from: 'bot', node: nextNode }]);
          
          if (nextNode.data.aguardar_resposta === false && nextNode.data.proxima_etapa_id) {
            setTimeout(() => {
              const nextNode2 = nodes.find(n => n.id === nextNode.data.proxima_etapa_id);
              if (nextNode2) {
                setCurrentNode(nextNode2);
                const msg2 = nextNode2.data.mensagem || nextNode2.data.label;
                setMessages(prev => [...prev, { text: msg2, from: 'bot', node: nextNode2 }]);
              }
            }, 800);
          }
        }
      }
    }, 500);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className={`w-[500px] h-[95vh] rounded-xl shadow-2xl overflow-hidden ${darkMode ? 'bg-zinc-800 border border-gray-700' : 'bg-white border border-gray-200'}`}>
        <div className={`flex items-center justify-between px-4 py-3 border-b ${darkMode ? 'border-gray-700 bg-zinc-900' : 'border-gray-200 bg-gray-50'}`}>
          <h3 className={`font-semibold text-base ${darkMode ? 'text-white' : 'text-gray-900'}`}>
            📱 Simulador WhatsApp
          </h3>
          <div className="flex items-center gap-2">
            <button
              onClick={handleReset}
              className={`px-3 py-1.5 text-sm rounded-lg transition-colors ${darkMode ? 'bg-zinc-700 hover:bg-zinc-600 text-white' : 'bg-gray-200 hover:bg-gray-300 text-gray-700'}`}
            >
              🔄 Reiniciar
            </button>
            <button
              onClick={onClose}
              className={`p-1.5 rounded-lg transition-colors ${darkMode ? 'text-gray-400 hover:text-white hover:bg-zinc-800' : 'text-gray-600 hover:text-gray-900 hover:bg-gray-200'}`}
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
        <div className="flex flex-col h-[calc(100%-3.5rem)]">
          <div className={`flex-1 p-4 overflow-y-auto ${darkMode ? 'bg-[#0b141a]' : 'bg-[#e5ddd5]'}`} style={{ backgroundImage: darkMode ? 'none' : 'url("data:image/svg+xml,%3Csvg width="100" height="100" xmlns="http://www.w3.org/2000/svg"%3E%3Cpath d="M0 0h100v100H0z" fill="%23e5ddd5"/%3E%3Cpath d="M20 10h60v2H20zm0 20h40v2H20zm0 20h50v2H20z" fill="%23d1c7b7" opacity=".1"/%3E%3C/svg%3E")' }}>
            <div className="space-y-2">
              {messages.map((msg, idx) => {
                const formattedMsg = resolverPreview(msg.text, fluxo?.variaveis);
                
                return (
                  <div key={idx} className={`flex ${msg.from === 'user' ? 'justify-end' : 'justify-start'}`}>
                    <div className={`relative max-w-[75%] ${msg.from === 'user' ? 'ml-auto' : ''}`}>
                      <div className={`rounded-lg p-3 shadow-sm relative ${msg.from === 'user' ? 'bg-[#DCF8C6]' : 'bg-white'}`}>
                        <div className="text-sm whitespace-pre-wrap break-words" style={{ wordBreak: 'break-word', color: '#000' }}>
                          {formattedMsg.split(/([*_~])([^*_~]+)\1/g).map((part, i) => {
                            if (i % 3 === 1) {
                              const marker = part;
                              const text = formattedMsg.split(/([*_~])([^*_~]+)\1/g)[i + 1];
                              if (marker === '*') return <strong key={i}>{text}</strong>;
                              if (marker === '_') return <em key={i}>{text}</em>;
                              if (marker === '~') return <s key={i}>{text}</s>;
                            }
                            if (i % 3 === 0) return part;
                            return null;
                          })}
                        </div>
                        <div className="text-[10px] text-gray-600 text-right mt-1">
                          {new Date().toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
              <div ref={messagesEndRef} />
            </div>
          </div>
          <div className={`p-3 border-t ${darkMode ? 'border-gray-700 bg-zinc-800' : 'border-gray-200 bg-[#f0f0f0]'}`}>
            {currentNode?.type === 'finalizar' ? (
              <div className={`text-center py-4 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                Conversa finalizada
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="flex gap-2">
                <input
                  type="text"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  placeholder="Digite sua mensagem..."
                  className={`flex-1 px-4 py-3 rounded-full border-2 focus:outline-none focus:border-green-500 ${darkMode ? 'bg-zinc-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
                />
                <button
                  type="submit"
                  className="px-5 py-3 rounded-full bg-green-600 hover:bg-green-700 text-white font-medium"
                >
                  ➤
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default WhatsAppSimulator;
